import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAwaitControl } from 'react-redux-await-control';
import { AGREEMENT_NAMES, AGREEMENTS_KEYS } from '~/common/constants';

import { Alert, Files, Flex, Form, RichText, Select, withStepperProps } from '~/ui/components';
import { LoadingSpinner } from '~/ui/assets/icons';
import { getContractsOfPerson, getFGTSOpportunity, getPersonDetail } from '~/store/people/actions';

import {
  getServiceChannels,
  getReasons,
  getTicketReasons,
  getTicketTeams,
  getTicketProducts,
  getTicketJourneys,
  getTicketStatus,
  getTicketResolutionReasons,
  getTicketJourneysSteps,
  getTicketResolvingAreas,
  getTicketCategories,
  getTicketClassificationRules,
} from '~/store/tickets/actions';
import { TicketData, TwilioInput } from '~/components';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import LoanType, { LOAN_TYPES } from '~/typings/enums/LoanType';
import ContractStatus from '~/typings/enums/ContractStatus';
import { Money } from '~/common';
import { Ticket, TicketClassificationRule } from '~/typings/entities/Ticket';
import { getLoggedUser } from '~/store/user/actions';
import toNumber from '~/common/masked/toNumber';
import { createSelectValues } from '~/common/selectValues';
import { MultiSelect } from '~/ui/components/MultiSelect/MultiSelect';
import {
  FGTSInitialJourneyStep,
  INSSInitialJourneyStep,
  filteredStatus,
  getJourneyId,
  resolveProductName,
  resolveSelectedJourney,
} from '~/common/tickets';
import { useDrawer } from '~/hooks/useDrawer';
import usePersistTicketParams from '~/hooks/usePersistTicketParams';
import RadioSelector from '~/ui/components/RadioSelector/RadioSelector';
import { TicketOrigin } from '~/typings/enums/Ticket/TicketOrigin';
import { FormWrapper, CustomButton } from '../TicketModal.styled';

type TicketFormProps = {
  setData?: (data: TicketData) => void;
  title?: string;
  data?: TicketData;
  isUpdating?: boolean;
  readOnlyFields?: string[];
  companyId?: number;
};

type ContractOption = { label: string; value: string; details?: string };

function toLinkedContractOption(contract: any): ContractOption {
  return {
    label: contract?.bankName ? `${contract.id} (${contract.bankName})` : `${contract.id}`,
    value: `${contract.id}`,
  };
}

export function toPersonContractOptions(contracts: any[]): ContractOption[] {
  return contracts.map((contract: any) => {
    const agreementName = contract?.agreementId
      ? ` / ${AGREEMENT_NAMES?.[contract.agreementId] || contract.agreementName}`
      : '';

    const isBenefitOrConsignedCard = ['BENEFIT_CARD_RCC', 'CONSIGNED_CARD_RMC'].includes(contract.loanType);
    const value = `${isBenefitOrConsignedCard ? contract.externalId : contract.contractId}`;
    const details = `${LoanType(contract.loanType)} / ${ContractStatus(contract.contractSituation)} / ${Money(
      contract.contractValue,
    )}${agreementName}`;

    return { label: `${value} (${details})`, value, details };
  });
}

export function mergeContractOptions(personOptions: ContractOption[], linkedContracts?: any[]): ContractOption[] {
  const available = (linkedContracts || []).filter((contract: any) => contract?.id != null);
  const linkByOptionValue = new Map<string, any>();

  const claimBy = (contractKey: (contract: any) => any) => {
    personOptions.forEach((option) => {
      if (linkByOptionValue.has(option.value)) return;

      const index = available.findIndex((contract: any) => {
        const key = contractKey(contract);
        return key != null && String(key) === option.value;
      });

      if (index >= 0) linkByOptionValue.set(option.value, available.splice(index, 1)[0]);
    });
  };

  claimBy((contract: any) => contract.id);
  claimBy((contract: any) => contract.externalId);

  const mergedOptions = personOptions.map((option) => {
    const link = linkByOptionValue.get(option.value);

    if (!link) return option;

    const value = String(link.id);
    return { ...option, value, label: option.details ? `${value} (${option.details})` : value };
  });

  return [...mergedOptions, ...available.map(toLinkedContractOption)];
}

function toLinkedContractPayload(contracts?: any[]) {
  return (contracts || [])
    .filter((contract: any) => contract?.id != null)
    .map((contract: any) => ({ id: Number(contract.id) }));
}

export function contractsForPayload({
  isUpdating,
  contractsChanged,
  submitted,
  linked,
}: {
  isUpdating: boolean;
  contractsChanged: boolean;
  submitted: any[];
  linked?: any[];
}) {
  return !isUpdating || contractsChanged ? submitted : toLinkedContractPayload(linked);
}

function sameContractIds(contracts: any[], ids: string[]) {
  const byText = (a: string, b: string) => a.localeCompare(b);
  const submitted = contracts.map((contract: any) => String(contract?.id)).sort(byText);
  const seeded = ids.map(String).sort(byText);
  return submitted.length === seeded.length && submitted.every((id, index) => id === seeded[index]);
}

export const TicketForm = withStepperProps<TicketFormProps>(
  ({ setData, stepper, title, data, isUpdating = false, readOnlyFields = [], companyId }) => {
    const { setConfig } = useDrawer();
    const form = useRef<WrappedFormUtils>();
    const seededContracts = useRef<string[]>(null);

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedJourney, setSelectedJourney] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [contactOrigin, setContactOrigin] = useState<string | null>(null);
    const [category, setCategory] = useState<number | null>(null);

    const getContractsOfPersonControl = useAwaitControl(getContractsOfPerson);
    const getServiceChannelsControl = useAwaitControl(getServiceChannels);
    const getReasonsControl = useAwaitControl(getReasons);
    const getTeamsControl = useAwaitControl(getTicketTeams);
    const getProductsControl = useAwaitControl(getTicketProducts);
    const getJourneysControl = useAwaitControl(getTicketJourneys);
    const getJourneysStepsControl = useAwaitControl(getTicketJourneysSteps);
    const personControl = useAwaitControl(getPersonDetail);
    const getLoggedUserControl = useAwaitControl(getLoggedUser);
    const getTicketStatusControl = useAwaitControl(getTicketStatus);
    const getTicketResolutionReasonsControl = useAwaitControl(getTicketResolutionReasons);
    const getFGTSOpportunityControl = useAwaitControl(getFGTSOpportunity);
    const getTicketAreasControl = useAwaitControl(getTicketResolvingAreas);
    const getTicketCategoriesControl = useAwaitControl(getTicketCategories);
    const getTicketClassificationRulesControl = useAwaitControl(getTicketClassificationRules);
    const getTicketReasonsControl = useAwaitControl(getTicketReasons);

    const loggedUser = getLoggedUserControl.result();
    const personDetail = personControl.result();
    const contractsPersonId = useMemo(() => {
      if (companyId) return undefined;

      return isUpdating ? data?.ticket?.personId ?? personDetail?.id : personDetail?.id ?? data?.ticket?.personId;
    }, [companyId, isUpdating, data?.ticket?.personId, personDetail?.id]);
    const personContracts = getContractsOfPersonControl.result(contractsPersonId).entries;
    const contracts = useMemo(() => (companyId ? [] : personContracts), [companyId, personContracts]);
    const sortedContracts = useMemo(
      () =>
        [...(contracts || [])].sort((a, b) => new Date(b.contractDate).getTime() - new Date(a.contractDate).getTime()),
      [contracts],
    );
    const reasons = getReasonsControl.result();
    const channels = getServiceChannelsControl.result();
    const person = personDetail;
    const teams = getTeamsControl.result();
    const products = getProductsControl.result();
    const journeys = getJourneysControl.result();
    const journeysSteps = getJourneysStepsControl.result();
    const status = getTicketStatusControl.result();
    const resolutionReasons = getTicketResolutionReasonsControl.result();
    const fgtsOpportunity = getFGTSOpportunityControl.result();
    const areas = getTicketAreasControl.result();
    const categories = getTicketCategoriesControl.result();
    const classificationRules: TicketClassificationRule[] = getTicketClassificationRulesControl.result();
    const allReasons = getTicketReasonsControl.result();

    const reasonsLoading = getReasonsControl.isRunning();
    const channelsLoading = getServiceChannelsControl.isRunning();
    const teamsLoading = getTeamsControl.isRunning();
    const productsLoading = getProductsControl.isRunning();
    const journeysLoading = getJourneysControl.isRunning();
    const contractLoading = getContractsOfPersonControl.isRunning(contractsPersonId);
    const contractsFailed = getContractsOfPersonControl.hasFailure(contractsPersonId);
    const contractsResolved = getContractsOfPersonControl.isSuccessful(contractsPersonId) || contractsFailed;
    const statusLoading = getTicketStatusControl.isRunning();
    const resolutionReasonsLoading = getTicketResolutionReasonsControl.isRunning();
    const categoriesLoading = getTicketCategoriesControl.isRunning();

    const { getPreFilledParam } = usePersistTicketParams(person.id);

    const getSessionParam = useCallback(
      (key: string) => (companyId ? null : getPreFilledParam(key)),
      [companyId, getPreFilledParam],
    );

    const contractOptions = useMemo(
      () => mergeContractOptions(toPersonContractOptions(sortedContracts), data?.ticket?.contracts),
      [sortedContracts, data?.ticket?.contracts],
    );

    const contactOriginInitialValue = useMemo(() => {
      if (isUpdating) {
        return data?.ticket?.contactOrigin || null;
      }

      return data?.ticket?.contactOrigin || getSessionParam('ticketContactOrigin') || 'ACTIVE';
    }, [data, isUpdating, getSessionParam]);

    const channelInitialValue = useMemo(() => {
      if (isUpdating) {
        return createSelectValues(channels, data?.ticket?.serviceChannelId);
      }

      return createSelectValues(channels, data?.ticket?.serviceChannelId || Number(getSessionParam('ticketChannel')));
    }, [data, isUpdating, channels, getSessionParam]);

    const protocolInitialValue = useMemo(() => {
      if (isUpdating) {
        return data?.ticket?.protocolTwilio || null;
      }

      return data?.ticket?.protocolTwilio || getSessionParam('twillioProtocol') || null;
    }, [data, isUpdating, getSessionParam]);

    const handleSelectionStatusChange = useCallback(
      (selectedStatus) => {
        setSelectedStatus(selectedStatus);
        form.current?.setFieldsValue({ justificationResolutionId: null });
      },
      [selectedStatus],
    );

    const filteredJourneys = useMemo(() => {
      let filteredItems = [];

      if (contactOrigin === TicketOrigin.RECEPTIVE) {
        const journeysByCategory = classificationRules
          .filter((rule) => rule.categoryId === category)
          .map((el) => el.journeyId);

        filteredItems = journeys?.filter((journey) => journeysByCategory.includes(journey.id));
      } else {
        filteredItems = journeys?.filter((journey) => journey.products?.includes(selectedProduct));
      }
      return filteredItems || [];
    }, [journeys, selectedProduct, contactOrigin, classificationRules, category]);

    const filteredResolutionReasons = useMemo(() => {
      const filteredItems = resolutionReasons?.filter(
        (reason) => reason.journeys?.includes(selectedJourney) && reason.status?.includes(selectedStatus),
      );
      return filteredItems || [];
    }, [resolutionReasons, selectedStatus, selectedJourney]);

    const handleProductChange = useCallback(
      ([loanType]) => {
        setSelectedProduct(loanType);
        if (loanType !== selectedProduct) {
          seededContracts.current = [];
          if (contactOrigin === TicketOrigin.RECEPTIVE) {
            form.current?.setFieldsValue({ contracts: [] });
          } else {
            form.current?.setFieldsValue({ contracts: [], journeyId: [], ticketReasonId: [] });
          }
        }
      },
      [setSelectedProduct, selectedProduct, contactOrigin],
    );

    const handleJourneyChange = useCallback(
      ([journey]) => {
        setSelectedJourney(journey);
        if (journey !== selectedJourney) {
          form.current?.setFieldsValue({ ticketReasonId: [] });
        }
      },
      [setSelectedJourney, selectedJourney],
    );

    const handleContactOriginChange = useCallback((value) => {
      setContactOrigin(value);

      if (value !== contactOrigin) {
        form.current?.setFieldsValue({
          categoryId: [],
          journeyId: [],
          ticketReasonId: [],
          justificationResolutionId: null,
        });
      }
    }, []);

    const handleCategoryChange = useCallback((value) => {
      setCategory(value.length === 1 ? value?.[0] : value);

      if (value !== category) {
        form.current?.setFieldsValue({ ticketReasonId: [], journeyId: [] });
      }
    }, []);

    const validateContractValue = (value) =>
      Array.isArray(value) ? value?.map((id: string) => ({ id: Number(id) })) : [{ id: Number(value) }];

    const validateSelectValue = (value, last = false) =>
      (Array.isArray(value) ? value[last ? value.length - 1 : 0] : value) || null;

    const handleSubmit = (values: any) => {
      const { channel, teamId, ticketScheduleDate: scheduleDate, files, ...rest } = values;
      const serviceChannelId = validateSelectValue(channel, true);
      const contractId = validateSelectValue(values?.contractId);
      const ticketScheduleDate = scheduleDate ? scheduleDate.format('YYYY-MM-DD HH:mm:ss') : null;
      const personId = contractsPersonId;
      const userCreated = loggedUser?.name;
      const userCreatedId = loggedUser?.id;
      const submittedContracts = companyId
        ? toLinkedContractPayload(data?.ticket?.contracts)
        : validateContractValue(values.contracts);
      const contractsChanged =
        !!data?.contractsChanged || !sameContractIds(submittedContracts, seededContracts.current || []);
      const contracts = contractsForPayload({
        isUpdating,
        contractsChanged,
        submitted: submittedContracts,
        linked: data?.ticket?.contracts,
      });
      const id = isUpdating ? data?.ticket?.id : undefined;
      const priorityTicket = data?.ticket?.priorityTicket;
      const conversationId = getSessionParam('conversationId');
      const team = validateSelectValue(teamId, true);

      const ticket: Ticket = {
        ...rest,
        ticketScheduleDate,
        serviceChannelId,
        personId,
        companyId,
        userCreated,
        userCreatedId,
        contractId,
        contracts,
        id,
        priorityTicket,
        conversationId,
        teamId: team,
      };

      if (values.categoryId != null) {
        [ticket.category] = categories.filter((item) => Number(item.id) === Number(values.categoryId));
      }

      setData?.({ ticket, files, contractsChanged });
      stepper.nextStep();
    };

    const contractProduct = useMemo(() => {
      if (data?.ticket?.product) {
        return [resolveProductName(data?.ticket?.product, products)];
      }

      if (sortedContracts.length > 0) {
        if (
          sortedContracts[0].agreementKey === AGREEMENTS_KEYS.CONSIGPRIV &&
          sortedContracts[0].loanType === LOAN_TYPES.REFIN
        ) {
          return ['CONSIGPRIV_REFIN'];
        }

        if (
          sortedContracts[0].agreementKey === AGREEMENTS_KEYS.CONSIGPRIV &&
          sortedContracts[0].loanType === LOAN_TYPES.PORTABILITY
        ) {
          return ['CONSIGPRIV_PORT'];
        }

        if (sortedContracts[0].agreementName === AGREEMENTS_KEYS.CONSIGPRIV) {
          return [sortedContracts[0].agreementName];
        }

        return [sortedContracts[0].loanType];
      }

      return null;
    }, [data, sortedContracts, products]);

    const getInitialJourney = useMemo(() => {
      const inss = ['FUTUREMARGIN', 'NEW', 'PORTABILITY', 'REFIN'];
      const qiTechOpportunity = fgtsOpportunity?.marginValues?.marginInformation?.marginCode;

      const operationStep = sortedContracts[0]?.operationStepType;

      const operationStepId = journeysSteps?.find((step) => step.operationStepType === operationStep)?.journeyId;

      if (contractProduct && contractProduct[0] === 'FGTS_NEW') {
        return FGTSInitialJourneyStep(person, filteredJourneys, qiTechOpportunity, sortedContracts, operationStepId);
      }

      if (contractProduct && inss.includes(contractProduct[0])) {
        return INSSInitialJourneyStep(person, filteredJourneys, sortedContracts, operationStepId);
      }

      if (contractProduct && contractProduct[0] === 'CLIX_CLEAN') {
        return getJourneyId('Cobrança', filteredJourneys);
      }

      return getJourneyId('Outros', filteredJourneys) || [];
    }, [contractProduct, person, sortedContracts, filteredJourneys, fgtsOpportunity, journeysSteps]);

    const filteredReasons = useMemo(() => {
      let filteredItems = [];

      if (contactOrigin === TicketOrigin.RECEPTIVE) {
        const reasonsByJourney = classificationRules
          .filter((rule) => rule.journeyId === selectedJourney && rule.categoryId === category)
          .map((el) => el.reasonId);

        filteredItems = allReasons?.filter((reason) => reasonsByJourney.includes(reason.id));
      } else {
        filteredItems = reasons?.filter(
          (reason) => reason.journeys?.includes(selectedJourney) && reason.products?.includes(selectedProduct),
        );
      }

      return filteredItems || [];
    }, [
      journeys,
      selectedJourney,
      selectedProduct,
      getInitialJourney,
      contactOrigin,
      reasons,
      allReasons,
      classificationRules,
      category,
    ]);

    const initialContracts = useMemo(() => {
      if (contractProduct?.[0] === 'CONSIGPRIV_PORT' && !data?.ticket?.contracts?.length) {
        return [];
      }

      if (data?.ticket?.contracts) {
        return data?.ticket?.contracts.map(({ id, externalId, contractId }) => String(id || externalId || contractId));
      }

      return sortedContracts[0]?.contractId ? [String(sortedContracts[0].contractId)] : [];
    }, [sortedContracts, contractProduct, data?.ticket?.contracts]);

    useEffect(() => {
      if (contractProduct) {
        setSelectedProduct(contractProduct[0]);
      }
    }, [contractProduct]);

    useEffect(() => {
      if (!data?.ticket?.journeyId) {
        setSelectedJourney((current) => resolveSelectedJourney(current, getInitialJourney[0], filteredJourneys));
      }
    }, [getInitialJourney]);

    const inputs = useMemo(() => {
      const mainInputs = [
        {
          id: 'contactOrigin',
          label: 'Origem do contato',
          initialValue: contactOriginInitialValue,
          input: (
            <RadioSelector
              label="Origem do contato"
              orientation="row"
              onChange={handleContactOriginChange}
              options={[
                { label: 'Ativo', value: 'ACTIVE' },
                { label: 'Receptivo', value: 'RECEPTIVE' },
              ]}
            />
          ),
          options: { rules: [{ required: true, message: 'Este campo é obrigatório!' }] },
        },
        {
          id: 'categoryId',
          label: 'Categoria',
          initialValue:
            data?.ticket?.categoryId || data?.ticket?.category?.id
              ? [data?.ticket?.categoryId || data?.ticket?.category?.id]
              : [],
          input: (
            <Select
              placeholder="Selecione a categoria"
              testId="category"
              options={categories}
              loading={categoriesLoading}
              onChange={handleCategoryChange}
              fieldNames={{ label: 'description', value: 'id' }}
            />
          ),
          options: { rules: [{ required: true, message: 'Este campo é obrigatório!' }] },
        },
        {
          id: 'product',
          label: 'Produto',
          initialValue: contractProduct,
          input: (
            <Select
              options={products}
              placeholder="Selecione o produto"
              onChange={handleProductChange}
              loading={productsLoading}
              fieldNames={{ label: 'description', value: 'name' }}
              testId="product"
            />
          ),
          options: { rules: [{ required: true, message: 'Este campo é obrigatório!' }] },
        },
        {
          id: 'contracts',
          label: 'Contrato (Opcional)',
          initialValue: initialContracts,
          input: (
            <MultiSelect
              options={contractOptions}
              placeholder="Selecione o contrato (Opcional)"
              loading={contractLoading}
              initialValue={initialContracts}
              testId="contracts"
            />
          ),
        },
        {
          id: 'journeyId',
          label: 'Etapa da jornada',
          initialValue: data?.ticket?.journeyId ? [data?.ticket?.journeyId] : getInitialJourney,
          input: (
            <Select
              options={filteredJourneys}
              placeholder="Selecione a etapa da jornada"
              loading={journeysLoading}
              onChange={handleJourneyChange}
              fieldNames={{ label: 'name', value: 'id' }}
              disabled={contactOrigin === TicketOrigin.RECEPTIVE ? !category : !selectedProduct}
              testId="journey"
              popupPlacement="left"
            />
          ),
          options: { rules: [{ required: true, message: 'Este campo é obrigatório!' }] },
        },
        {
          id: 'ticketReasonId',
          label: 'Motivo da abertura',
          initialValue: data?.ticket?.ticketReasonId ? [data?.ticket?.ticketReasonId] : [],
          input: (
            <Select
              options={filteredReasons}
              placeholder="Selecione o motivo"
              loading={reasonsLoading}
              fieldNames={{ label: 'name', value: 'id' }}
              disabled={!selectedJourney}
              testId="opening-reason"
              popupPlacement="left"
            />
          ),
          options: { rules: [{ required: true, message: 'Este campo é obrigatório!' }] },
        },
        {
          id: 'channel',
          label: 'Canal',
          initialValue: channelInitialValue,
          input: (
            <Select
              options={channels}
              placeholder="Selecione o canal"
              loading={channelsLoading}
              fieldNames={{ label: 'name', value: 'id' }}
              testId="channel"
              popupPlacement="left"
            />
          ),
          options: { rules: [{ required: true, message: 'Este campo é obrigatório!' }] },
        },
        {
          id: 'teamId',
          label: <>Time</>,
          labelHelp: {
            title: 'Selecione seu Time',
            content: 'Por favor, selecione o nome do time ao qual você pertence aqui na meutudo.',
          },
          initialValue: data?.ticket?.teamId ? [data?.ticket?.teamId] : null,
          input: (
            <Select
              options={teams}
              placeholder="Selecione o time"
              loading={teamsLoading}
              fieldNames={{ label: 'name', value: 'id' }}
              testId="team-id"
              popupPlacement="left"
            />
          ),
          options: { rules: [{ required: true, message: 'Este campo é obrigatório!' }] },
        },
        {
          id: 'ticketStatusId',
          label: 'Status de resolução',
          initialValue: data?.ticket?.ticketStatusId || null,
          input: (
            <RadioSelector
              label="Status de resolução"
              options={filteredStatus(status, 'CREATE_TICKET')}
              orientation="column"
              onChange={handleSelectionStatusChange}
            />
          ),
          options: {
            rules: [{ required: true, message: 'Selecione o status de resolução' }],
          },
        },
        {
          id: 'justificationResolutionId',
          label: 'Justificativa da resolução',
          initialValue: data?.ticket?.justificationResolutionId ? [data?.ticket?.justificationResolutionId] : null,
          input: (
            <Select
              options={filteredResolutionReasons}
              placeholder="Selecione a justificativa da resolução"
              loading={resolutionReasonsLoading}
              disabled={!filteredResolutionReasons?.length}
              fieldNames={{ label: 'justification', value: 'id' }}
              testId="justification-resolution"
              popupPlacement="left"
            />
          ),
          options: {
            rules: [
              {
                required: !!filteredResolutionReasons?.length,
                message: 'Selecione o status de resolução',
              },
            ],
          },
        },
        {
          id: 'protocolTwilio',
          initialValue: protocolInitialValue,
          input: <TwilioInput defaultChecked={!!protocolInitialValue} />,
          options: {
            normalize: toNumber,
            rules: [{ max: 49, message: 'O protocolo deve conter no max 50 caracteres' }],
          },
        },
        {
          id: 'files',
          initialValue: data?.files,
          skipArrayFormat: true,
          input: <Files />,
        },
        {
          id: 'description',
          label: 'Descrição',
          input: <RichText placeholder="Digite aqui a descrição do ticket" />,
          initialValue: data?.ticket?.description,
          options: { rules: [{ required: true, message: 'Este campo é obrigatório!' }] },
        },
      ];

      const visibleInputs = companyId ? mainInputs.filter((input) => input.id !== 'contracts') : mainInputs;

      if (contactOrigin !== TicketOrigin.RECEPTIVE) {
        return visibleInputs.filter((input) => input.id !== 'categoryId');
      }

      return visibleInputs;
    }, [
      data,
      reasons,
      channels,
      teams,
      products,
      journeys,
      reasonsLoading,
      resolutionReasons,
      filteredResolutionReasons,
      resolutionReasonsLoading,
      statusLoading,
      status,
      filteredJourneys,
      filteredReasons,
      channelsLoading,
      teamsLoading,
      productsLoading,
      journeysLoading,
      selectedProduct,
      selectedJourney,
      contactOriginInitialValue,
      channelInitialValue,
      protocolInitialValue,
      contractProduct,
      categories,
      categoriesLoading,
      contactOrigin,
      companyId,
      contractOptions,
      contractLoading,
      initialContracts,
    ]);

    const processedInputs = useMemo(() => {
      if (!readOnlyFields?.length) return inputs;
      return inputs.map((input) => {
        if (readOnlyFields.includes(input.id)) {
          return {
            ...input,
            input: React.cloneElement(input.input, { disabled: true }),
            options: { ...input.options, rules: [] },
          };
        }
        return input;
      });
    }, [inputs, readOnlyFields]);

    useEffect(() => {
      if (!channels?.length) getServiceChannelsControl.start();
      if (!reasons?.length) getReasonsControl.start();
      if (!teams?.length) getTeamsControl.start();
      if (!products?.length) getProductsControl.start();
      if (!journeys?.length) getJourneysControl.start();
      if (!journeysSteps?.length) getJourneysStepsControl.start();
      if (!status?.length) getTicketStatusControl.start();
      if (!resolutionReasons?.length) getTicketResolutionReasonsControl.start();
      if (!areas?.length) getTicketAreasControl.start();
      if (!categories?.length) getTicketCategoriesControl.start();
      if (!classificationRules?.length) getTicketClassificationRulesControl.start();
      if (!allReasons?.length) getTicketReasonsControl.start();

      if (data?.ticket?.product) {
        setSelectedProduct(data.ticket.product);
      }

      if (data?.ticket?.journeyId) {
        setSelectedJourney(data.ticket.journeyId);
      }

      if (data?.ticket?.ticketStatusId) {
        setSelectedStatus(data.ticket.ticketStatusId);
      }

      if (data?.ticket?.categoryId || data?.ticket?.category?.id) {
        setCategory(data.ticket.categoryId || data?.ticket?.category?.id);
      }

      if (data?.ticket?.contactOrigin) {
        setContactOrigin(data.ticket.contactOrigin);
      }

      setConfig('ticket-modal', {
        title: isUpdating ? 'Editar ticket' : title,
        backButton: false,
      });
    }, [isUpdating]);

    const isEditDataReady = useMemo(() => {
      if (!isUpdating) return true;

      const baseReady = !!(
        products?.length &&
        channels?.length &&
        status?.length &&
        teams?.length &&
        journeys?.length &&
        reasons?.length &&
        selectedProduct
      );

      const contractsReady = !!companyId || !data?.ticket?.contracts?.length || (!contractLoading && contractsResolved);

      return baseReady && contractsReady;
    }, [
      isUpdating,
      products,
      channels,
      status,
      teams,
      journeys,
      reasons,
      selectedProduct,
      companyId,
      contractLoading,
      contractsResolved,
      data?.ticket?.contracts,
    ]);

    useEffect(() => {
      if (!contractsPersonId) return;

      getContractsOfPersonControl.start({ id: contractsPersonId }, { actionId: contractsPersonId });
    }, [contractsPersonId]);

    if (seededContracts.current === null || !isEditDataReady) {
      seededContracts.current = initialContracts;
    }

    if (!isEditDataReady) {
      return (
        <FormWrapper data-testid="ticket-form-wrapper">
          <Flex align="center" justify="center" height="200px">
            <LoadingSpinner width={24} height={24} />
          </Flex>
        </FormWrapper>
      );
    }

    return (
      <FormWrapper data-testid="ticket-form-wrapper">
        {isUpdating && (
          <Alert
            label="Você está editando um ticket aberto; apenas times autorizados podem fazer isso."
            status="warning"
          />
        )}
        {!companyId && contractsFailed && (
          <Alert
            label="Não foi possível carregar os contratos da pessoa; a lista pode estar incompleta."
            status="warning"
          />
        )}
        <Form ref={form} name="reset-form" onSubmit={handleSubmit} inputs={processedInputs}>
          <CustomButton rounded fullWidth type="submit" data-testid="ticket-form-button">
            Revisar a solicitação
          </CustomButton>
        </Form>
      </FormWrapper>
    );
  },
);
